import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Hammer from "hammerjs";
import _ from "lodash";

import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { actions } from "reducer/reducer";

import { Hidden, Typography, Fab, SwipeableDrawer } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import CasinoIcon from "@material-ui/icons/Casino";
import VolumeOffIcon from "@material-ui/icons/VolumeOff";
import VolumeUpIcon from "@material-ui/icons/VolumeUp";
import hexRgb from "hex-rgb";

import { diceBoxController as diceBox } from "../diceBoxController";
import DiceBoxControlPanel from "../DiceBoxControlPanel/DiceBoxControlPanel";
import ResultsBanner from "../ResultsBanner/ResultsBanner";
import NoDiceModalMessage from "../NoDiceModalMessage/NoDiceModalMessage";

import {
  defaultRollOptions as options,
  DROP_TYPES,
  RESULT_TYPES,
} from "../settings";

import "./DiceBox.css";

const makeCancelable = (promise) => {
  let hasCanceled_ = false;

  const wrappedPromise = new Promise((resolve, reject) => {
    promise.then(
      (val) => (hasCanceled_ ? reject({ isCanceled: true }) : resolve(val)),
      (error) => (hasCanceled_ ? reject({ isCanceled: true }) : reject(error))
    );
  });

  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    },
  };
};

diceBox.initDiceBox();

const { showAppModal: _showAppModal, setModalMessage: _setModalMessage } =
  actions.modal;
const { updateDiceBoxState: _updateDiceBoxState } = actions.diceBox;
const { toggleMute: _toggleMute } = actions.mute;

const selectSounds = createSelector((state) => state.sounds, _.identity);
const selectPreferences = createSelector(
  (state) => state.preferences,
  _.identity
);
const selectDiceBoxInfo = createSelector((state) => state.diceBox, _.identity);
const selectMute = createSelector((state) => state.mute, _.identity);

const useStyles = makeStyles((theme) => {
  const [r, g, b] = hexRgb(theme.palette.background.paper, { format: `array` });

  return {
    instructions: {
      position: `absolute`,
      top: `40%`,
      width: `100%`,
      textAlign: `center`,
      backgroundImage: `linear-gradient(to right, rgba(${r},${g},${b},0.5), ${theme.palette.background.paper}, rgba(${r},${g},${b},0.5))`,
    },
    fabWrapper: {
      position: `fixed`,
      width: `100vw`,
      bottom: `0`,
      left: `0`,
      marginBottom: `24px`,
      display: `flex`,
      justifyContent: `center`,
      pointerEvents: `none`,
    },
    fab: {
      pointerEvents: `auto`,
    },
  };
});

const DiceBox = ({ closeSpeedDial }) => {
  const classes = useStyles();

  const preferences = useSelector(selectPreferences);
  const sounds = useSelector(selectSounds);
  const diceBoxInfo = useSelector(selectDiceBoxInfo);
  const mute = useSelector(selectMute);
  const dispatch = useDispatch();

  // Work around weird dependency issue causing stale closures
  const muteRef = useRef(true);
  useEffect(() => {
    muteRef.current = mute;
  }, [mute]);

  const [setModalMessage, showAppModal, toggleMute, updateDiceBoxState] =
    useMemo(
      () => [
        (...args) => dispatch(_setModalMessage(...args)),
        (...args) => dispatch(_showAppModal(...args)),
        (...args) => dispatch(_toggleMute(...args)),
        (...args) => dispatch(_updateDiceBoxState(...args)),
      ],
      [dispatch]
    );

  const canvas = useRef(null);
  const hammertime = useRef(null);
  const rollPromise = useRef(null);
  const rollInProgress = useRef(false);
  // Hammer.js does something weird w/ the listeners -- not sure how to unsubscribe from the
  // DOM Events on click/tap, so using refs to avoid stale closures
  const diceBoxInfoRef = useRef(null);
  const closeSpeedDialRef = useRef(null);
  const [results, setResults] = useState([]);
  const [displayResults, setDisplayResults] = useState(<></>);
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const [showInstructions, setShowInstructions] = useState(true);

  const { diceToRoll, dicePoolDieType, modifier, selectedOption } = diceBoxInfo;
  diceBoxInfoRef.current = diceBoxInfo;
  closeSpeedDialRef.current = closeSpeedDial;

  const parseRollResults = useCallback((results) => {
    const { diceToRoll, dicePoolDieType, modifier, selectedOption } =
      diceBoxInfoRef.current;
    const currentOption = options[selectedOption];
    let displayResults;
    switch (currentOption.resultType) {
      case RESULT_TYPES.ADD:
        displayResults = _.chain(results)
          .sortBy((el) => -el)
          .thru((results) => {
            switch (currentOption.drop) {
              case DROP_TYPES.LOWEST:
                return _.dropRight(results, currentOption.dropNumber);
              case DROP_TYPES.HIGHEST:
                return _.drop(results, currentOption.dropNumber);
              case DROP_TYPES.NONE:
              default:
                return results;
            }
          })
          .thru((results) => {
            if (0 === modifier) {
              if (1 === _.size(diceToRoll) || 15 < _.size(diceToRoll)) {
                return `${_.sum(results)}`;
              } else {
                return `${_.join(results, ` + `)} = ${_.sum(results)}`;
              }
            } else {
              if (15 >= _.size(diceToRoll)) {
                return (
                  <span>
                    {_.join(results, ` + `)}
                    {modifier > 0 ? (
                      <span style={{ color: `green` }}>
                        {` `}+ {Math.abs(modifier)}
                      </span>
                    ) : (
                      <span style={{ color: `red` }}>
                        {` `}&ndash; {Math.abs(modifier)}
                      </span>
                    )}
                    {` `}= {_.sum(results) + modifier}
                  </span>
                );
              } else {
                return (
                  <span>
                    {_.sum(results)}
                    {modifier > 0 ? (
                      <span style={{ color: `green` }}>
                        {` `}+ {Math.abs(modifier)}
                      </span>
                    ) : (
                      <span style={{ color: `red` }}>
                        {` `}&ndash; {Math.abs(modifier)}
                      </span>
                    )}
                    {` `}= {_.sum(results) + modifier}
                  </span>
                );
              }
            }
          })
          .thru((results) => (
            <Typography variant="h4" component="span">
              {results}
            </Typography>
          ))
          .value();
        break;
      case RESULT_TYPES.POOL:
        displayResults = _.chain(results)
          .groupBy()
          .map((val, key) => {
            const dieSides = _.parseInt(_.replace(dicePoolDieType, `d`, ``));
            let color = null;
            if (_.parseInt(key) <= Math.floor(dieSides / 3)) color = `red`;

            if (_.parseInt(key) > Math.floor((dieSides * 2) / 3))
              color = `green`;
            return (
              <Typography
                key={key}
                variant="h5"
                component="span"
                style={{ flexBasis: `35%` }}
              >
                {key}s<span style={{ color }}> x{val.length}</span>
              </Typography>
            );
          })
          .reverse()
          .thru((results) => (
            <div
              style={{
                display: `flex`,
                flexWrap: `wrap`,
                justifyContent: `center`,
              }}
            >
              {results}
            </div>
          ))
          .value();
        break;
      case RESULT_TYPES.PERCENTAGE:
        displayResults = _.chain(results)
          .thru((results) => {
            // first result is 100s dice, second is 1s
            const tensDigit = results[0];
            const onesDigit = results[1] % 10;
            let sum = tensDigit + onesDigit;
            if (0 === sum) sum = 100;
            return `${sum}%`;
          })
          .thru((results) => (
            <Typography variant="h5" component="span">
              {results}
            </Typography>
          ))
          .value();
        break;
      case RESULT_TYPES.RAW:
      default:
        displayResults = _.chain(results)
          .sortBy((el) => -el)
          .join(`, `)
          .thru((results) => (
            <Typography variant="h5" component="span">
              {results}
            </Typography>
          ))
          .value();
        break;
    }
    return displayResults;
  }, []);

  const rollDice = useCallback(
    (swipeInfo = {}) => {
      if (rollInProgress.current) return;
      const { diceToRoll } = diceBoxInfoRef.current;

      if (_.isEmpty(diceToRoll)) {
        setModalMessage({ component: NoDiceModalMessage });
        showAppModal();
        return;
      }

      rollInProgress.current = true;
      setResults([]);
      setShowInstructions(false);

      const rollSoundVersion = _.random(1, 3);
      const rollSoundKey = _.cond([
        [
          (number) => number <= 6,
          (number) => `${number}_standard_v${rollSoundVersion}`,
        ],
        [(number) => number <= 11, () => `6_standard_v${rollSoundVersion}`],
        [_.stubTrue, () => `16_standard_v${rollSoundVersion}`],
      ])(diceToRoll.length);

      _.delay(() => {
        !muteRef.current && sounds[rollSoundKey] && sounds[rollSoundKey].play();
      }, 300);

      rollPromise.current = makeCancelable(
        diceBox.rollDice({ diceTypes: diceToRoll, swipeInfo })
      );
      rollPromise.current.promise
        .then((results) => {
          const displayResults = parseRollResults(results);

          rollInProgress.current = false;
          setResults(results);
          setDisplayResults(displayResults);
        })
        .catch((err) => {
          console.error(err);
        });
    },
    [parseRollResults, setModalMessage, showAppModal, sounds]
  );

  const onOptionChange = ({
    selectedOption,
    diceToRoll,
    dicePoolDieType = `d6`,
    modifier = 0,
  }) => {
    const _results = _.isEqual(diceToRoll, diceBoxInfo.diceToRoll)
      ? results
      : [];
    const _displayResults = _.isEqual(diceToRoll, diceBoxInfo.diceToRoll) ? (
      displayResults
    ) : (
      <> </>
    );

    setResults(_results);
    setDisplayResults(_displayResults);

    // Redux link
    updateDiceBoxState({
      state: {
        selectedOption,
        diceToRoll,
        dicePoolDieType,
        modifier,
      },
    });
  };

  useEffect(() => {
    // Enable Swiping
    hammertime.current = new Hammer(canvas.current);
    hammertime.current.get(`swipe`).set({ direction: Hammer.DIRECTION_ALL });

    // Attach diceBoxSimultion to the DOM
    diceBox.attachDiceBoxToCanvas({
      window,
      canvas: canvas.current,
    });
    diceBox.resetDiceBoxDisplay(preferences);
    return () => {
      if (rollPromise.current) rollPromise.current.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resizeListener = () => {
      diceBox.resetDiceBoxDisplay(preferences);
    };

    window.addEventListener(`resize`, resizeListener, false);

    return () => {
      window.removeEventListener(`resize`, resizeListener, false);
    };
  }, [preferences]);

  useEffect(() => {
    const handleSwipe = (evt) => {
      closeSpeedDialRef.current();
      const swipeVector = { x: evt.deltaX, y: -evt.deltaY };
      const swipeTime = evt.deltaTime;
      rollDice({ swipeVector, swipeTime });
    };
    const handleTap = (evt) => {
      rollDice();
    };

    hammertime.current.on(`swipe`, handleSwipe);
    hammertime.current.on(`tap`, handleTap);
    // rollDice has dependencies, but they should be stable over the app session
    // if not, this will break
  }, [rollDice]);

  return (
    <>
      <div
        id="canvas-container"
        style={{
          maxHeight: `calc( 100vh - 44px )`,
          position: `relative`,
        }}
      >
        <Hidden smDown implementation="css">
          <DiceBoxControlPanel
            options={options}
            selectedOption={selectedOption}
            diceToRoll={diceToRoll}
            dicePoolDieType={dicePoolDieType}
            modifier={modifier}
            onOptionChange={onOptionChange}
            displayStyle="fixed"
          />
        </Hidden>
        <div ref={canvas} id="canvas">
          {!!results.length && (
            <ResultsBanner displayResults={displayResults} />
          )}
          {showInstructions && (
            <div className={classes.instructions}>
              <Typography variant="h6" component="span">
                Click, tap or swipe here.
              </Typography>
            </div>
          )}
        </div>
        <div
          style={{
            position: `fixed`,
            bottom: `21px`,
            left: `16px`,
            color: `red`,
            display: `flex`,
          }}
        >
          <Hidden smDown implementation="css">
            <div style={{ width: `300px` }} />
          </Hidden>
          <Fab
            color={mute ? "inherit" : "primary"}
            onClick={() => {
              toggleMute();
            }}
          >
            {mute ? (
              <VolumeOffIcon color="primary" style={{ marginRight: `4px` }} />
            ) : (
              <VolumeUpIcon style={{ marginRight: `4px` }} />
            )}
          </Fab>
        </div>
        <Hidden mdUp implementation="css">
          <div className={classes.fabWrapper}>
            <Fab
              className={classes.fab}
              color="primary"
              variant="extended"
              onClick={() => {
                setShowMobileDrawer(true);
                setResults([]);
              }}
            >
              <CasinoIcon style={{ marginRight: `4px` }} />
              Change Dice
            </Fab>
          </div>
        </Hidden>
        <SwipeableDrawer
          anchor="left"
          swipeAreaWidth={0}
          open={showMobileDrawer}
          onOpen={_.noop}
          onClose={() => {
            setShowMobileDrawer(false);
          }}
        >
          <DiceBoxControlPanel
            options={options}
            selectedOption={selectedOption}
            diceToRoll={diceToRoll}
            dicePoolDieType={dicePoolDieType}
            modifier={modifier}
            onOptionChange={onOptionChange}
            displayStyle="drawer"
          />
        </SwipeableDrawer>
      </div>
    </>
  );
};

export default DiceBox;
